import type { Definition } from '@/features/content/services/dictionaryService';
import { CloseIcon, IconButton, Pill, SpeakerIcon, Spinner } from '@/shared/ui';

/*
 * Behind a real dictionary a lookup takes time and can come back empty or fail,
 * so the popover opens on the tapped word and fills in afterwards. It carries
 * the word in every state: the header is what tells the viewer which tap this
 * card is answering.
 */
export type DefinitionLookup =
  | { word: string; status: 'loading' }
  | { word: string; status: 'found'; entry: Definition }
  | { word: string; status: 'missing' }
  | { word: string; status: 'error' };

export type DefinitionPopoverProps = {
  lookup: DefinitionLookup;
  onClose: () => void;
};

/** The word card: pronunciation, part of speech, meaning, and examples. */
export function DefinitionPopover({ lookup, onClose }: DefinitionPopoverProps) {
  const entry = lookup.status === 'found' ? lookup.entry : null;
  const audioUrl = entry?.audioUrl;

  return (
    <aside className="definition" aria-label={`Definition of ${lookup.word}`}>
      <header className="definition__head">
        <IconButton
          label={`Pronounce ${lookup.word}`}
          size="sm"
          variant="ghost"
          className="definition__audio"
          // Not every entry carries audio; a button that does nothing when
          // pressed is worse than one that is visibly unavailable.
          disabled={!audioUrl}
          onClick={() => {
            if (audioUrl) void new Audio(audioUrl).play().catch(() => {});
          }}
        >
          <SpeakerIcon size={18} />
        </IconButton>

        {/* The dictionary's headword once it answers — "ran" resolves to "run",
            and showing the tapped spelling would misattribute the definition. */}
        <h4 className="definition__word">{entry?.word ?? lookup.word}</h4>
        {entry ? (
          <Pill tone="green" size="sm">
            {entry.partOfSpeech}
          </Pill>
        ) : null}
        {entry?.pronunciation ? (
          <span className="definition__ipa">{entry.pronunciation}</span>
        ) : null}

        <IconButton label="Close definition" size="sm" variant="ghost" onClick={onClose}>
          <CloseIcon size={16} />
        </IconButton>
      </header>

      {lookup.status === 'loading' ? (
        <div className="definition__pending">
          <Spinner size={20} label={`Looking up ${lookup.word}`} />
        </div>
      ) : null}

      {/* Nobody has defined this word, versus we could not go and ask. Worth
          separating: one is answered, and the other is worth trying again. */}
      {lookup.status === 'missing' ? (
        <p className="definition__note">No definition found for “{lookup.word}”.</p>
      ) : null}

      {lookup.status === 'error' ? (
        <p className="definition__note">
          The dictionary could not be reached. It may be your connection, or the service
          may be busy — tap the word again to retry.
        </p>
      ) : null}

      {entry ? (
        <>
          <div className="definition__section">
            <h5 className="definition__label">Definition</h5>
            <p className="definition__text">{entry.definition}</p>
          </div>

          {entry.examples.length > 0 ? (
            <div className="definition__section">
              <h5 className="definition__label">Examples</h5>
              {entry.examples.map((example) => (
                <p className="definition__example" key={example}>
                  “{example}”
                </p>
              ))}
            </div>
          ) : null}
        </>
      ) : null}
    </aside>
  );
}
